import React, { useContext, useState } from 'react';
import { Trans } from '@lingui/react';
import * as R from 'ramda';
import styled from 'styled-components';
import Media from 'react-media';
import UseDeepCompareEffect from 'use-deep-compare-effect';
import DeviceContext from '@lib/contexts/deviceContext';
import Accordion from '@components/Accordion';
import { useQuery } from '@apollo/client';
import Divider from '@components/Divider';
import Label from '@components/form/Label';
import { Box, Flex } from '@components/layout/Grid';
import MenuItem from '@components/MenuItem';
import Tag from '@components/Tag';
import SearchFiltersModal from '@organisms/modals/SearchFiltersModal';
import Slick from '@organisms/Slick';
import { GET_VIDEO_SETTINGS } from '@lib/apollo';

const SelectedTerms = styled.div`
  overflow: hidden;
`;

const SelectedTermsComponent = ({
  selectedTerms,
  modifiedTaxonomies,
  handleSelectTerm,
  clearAll,
  isMobile,
}) => {
  const [selectedTaxs, setSelectedTaxs] = useState([]);
  UseDeepCompareEffect(() => {
    const taxonomies = [];
    (modifiedTaxonomies || []).forEach(taxonomy => {
      const selectedTaxonomies = taxonomy.terms.filter(e => e.selected);
      if (selectedTaxonomies && selectedTaxonomies.length > 0) {
        taxonomies.push(
          selectedTaxonomies.map(term => (
            <Box mr="10px" mb="10px" key={term.slug}>
              <Tag
                isSelected={true}
                term={term.slug}
                taxonomy={taxonomy.slug}
                onClick={() => {
                  handleSelectTerm(term, taxonomy.slug);
                }}
              >
                {term.name}
              </Tag>
            </Box>
          ))
        );
      }
    });
    setSelectedTaxs(taxonomies);
  }, [selectedTerms, modifiedTaxonomies]);

  if (isMobile && selectedTaxs.length > 0) {
    return (
      <React.Fragment>
        <Box mb={20} as={Divider} />
        <Box mb="20px" alignItems="center">
          <Slick>{selectedTaxs}</Slick>
        </Box>
      </React.Fragment>
    );
  }

  if (selectedTaxs.length > 0) {
    return (
      <React.Fragment>
        <Box mb={20}>
          <Divider />
        </Box>

        <Flex mb="10px" alignItems="start">
          <Box mr="20px" mb="10px" onClick={clearAll} flex="0 0 auto">
            <Label>
              <Trans id="metadata.clear_all" message={'Clear all'} />
            </Label>
          </Box>
          <Box flex="1">
            <Flex flexWrap="wrap" id={'selected_taxonomies'}>
              {selectedTaxs}
            </Flex>
          </Box>
        </Flex>
      </React.Fragment>
    );
  }

  return null;
};

const SearchFilter = props => {
  const { taxonomies, selectedTerms } = props;
  const { loading, data } = useQuery(GET_VIDEO_SETTINGS, { ssr: false });
  const [isModalOpen, openModal] = useState(false);
  const [selectedTaxonomySlug, setSelectedTaxonomySlug] = useState(null);
  const [modifiedTaxonomies, modifyTaxonomies] = useState(taxonomies);
  const { isDeviceMobile } = useContext(DeviceContext);

  UseDeepCompareEffect(() => {
    const newTaxonomies = taxonomies.map(_tax => {
      const tax = { ..._tax };
      tax.terms = tax.terms.map(_term => {
        const term = { ..._term };
        if (selectedTerms[tax.slug]?.includes(term.slug)) {
          term.selected = true;
        } else {
          term.selected = false;
        }
        return term;
      });

      return tax;
    });
    modifyTaxonomies(newTaxonomies);
  }, [selectedTerms, taxonomies]);

  const handleSelectTaxonomy = taxonomy => {
    setSelectedTaxonomySlug(
      selectedTaxonomySlug === taxonomy.slug ? null : taxonomy.slug
    );
  };

  // NOTE: this should probably be lifted up
  // no need to update object here send it to get it back from the parent
  const handleSelectTerm = (term, taxonomy = selectedTaxonomySlug) => {
    // Handle click term here
    const terms = R.clone(selectedTerms);
    if (terms[taxonomy]?.includes(term.slug)) {
      // Unselect term
      terms[taxonomy].splice(terms[taxonomy].indexOf(term.slug), 1);
      if (terms[taxonomy].length === 0) {
        delete terms[taxonomy];
      }
    } else {
      // Select term
      if (terms[taxonomy]) {
        terms[taxonomy].push(term.slug);
      } else {
        terms[taxonomy] = [term.slug];
      }
    }
    props.onChange(terms);
  };

  const clearAll = () => {
    props.onChange([]);
  };

  if (loading) {
    return null;
  }

  return (
    <React.Fragment>
      <Media query={{ maxWidth: 767 }} defaultMatches={isDeviceMobile}>
        {matches =>
          matches ? (
            <React.Fragment>
              <Box mb="20px" width={1}>
                <Slick>
                  {props.taxonomies.map(taxonomy => (
                    <MenuItem
                      key={taxonomy.slug}
                      isSelected={selectedTaxonomySlug === taxonomy.slug}
                      onClick={() => {
                        handleSelectTaxonomy(taxonomy);
                        openModal(true);
                      }}
                    >
                      {taxonomy.name}
                    </MenuItem>
                  ))}
                </Slick>
              </Box>
              <Flex as={SelectedTerms} mr={-20}>
                <SelectedTermsComponent
                  {...{
                    selectedTerms,
                    modifiedTaxonomies,
                    handleSelectTerm,
                    clearAll,
                  }}
                  isMobile
                />
              </Flex>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mb="20px"
              >
                <Box>
                  {props.taxonomies.map(taxonomy => (
                    <MenuItem
                      key={taxonomy.slug}
                      isSelected={taxonomy.slug === selectedTaxonomySlug}
                      onClick={() => {
                        handleSelectTaxonomy(taxonomy);
                      }}
                    >
                      {taxonomy.name}
                    </MenuItem>
                  ))}
                </Box>
              </Flex>
              <TaxonomiesList
                selectedTaxonomySlug={selectedTaxonomySlug}
                modifiedTaxonomies={modifiedTaxonomies}
                onSelectTerm={handleSelectTerm}
              />
              <SelectedTermsComponent
                {...{
                  selectedTerms,
                  modifiedTaxonomies,
                  handleSelectTerm,
                  clearAll,
                }}
                isMobile={false}
              />
            </React.Fragment>
          )
        }
      </Media>

      {/* Modal */}
      <SearchFiltersModal
        taxonomies={modifiedTaxonomies}
        selectedTaxonomySlug={selectedTaxonomySlug}
        open={isModalOpen}
        handleClose={() => {
          openModal(false);
          setSelectedTaxonomySlug(null);
        }}
        handleClickTerm={handleSelectTerm}
        clearAll={clearAll}
      />
    </React.Fragment>
  );
};

const TaxonomiesList = props => {
  const selected =
    props.selectedTaxonomySlug &&
    props.modifiedTaxonomies.find(
      taxonomy => taxonomy.slug === props.selectedTaxonomySlug
    );

  return (
    <Accordion
      isOpen={selected}
      head=""
      body={
        selected ? (
          <Flex flexWrap="wrap">
            {selected.terms.map(term => (
              <Box key={term.slug} mr="10px" mb="10px">
                <Tag
                  important={term.important}
                  isSelected={term.selected}
                  onClick={() => {
                    props.onSelectTerm(term);
                  }}
                >
                  {term.name}
                </Tag>
              </Box>
            ))}
          </Flex>
        ) : null
      }
    />
  );
};
export default SearchFilter;
